import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { LANDING_SCROLL_ROOT_ID } from '@pages/LandingPage/model/scroll';

import { LandingVisionItem } from './LandingVisionItem';
import { LandingVisionProgressIndicator } from './LandingVisionProgressIndicator';
import { visionItems } from '../../model/content';

type VisionTrackAnimationState = 'hidden' | 'show' | 'exit';

interface LandingVisionSectionProps {
  onSpheresSceneIndexChange: (sceneIndex: number) => void;
}

const visionTrackKeyframes = [
  { progress: 0, state: 'hidden' },
  { progress: 0.05, state: 'show' },
  { progress: 0.95, state: 'exit' },
] as const satisfies readonly { progress: number; state: VisionTrackAnimationState }[];

const visionTrackShowThreshold =
  visionTrackKeyframes.find((keyframe) => keyframe.state === 'show')?.progress ?? 0;

const visionTrackExitThreshold =
  visionTrackKeyframes.find((keyframe) => keyframe.state === 'exit')?.progress ?? 1;

const clampUnitInterval = (value: number) => Math.min(1, Math.max(0, value));

const visionTrackViewportSpans = visionItems.map((_, index) =>
  index === visionItems.length - 1 ? 1.5 : 1
);

const totalVisionTrackViewportSpans = visionTrackViewportSpans.reduce(
  (total, span) => total + span,
  0
);

const visionTrackLengthMobile = `${String(totalVisionTrackViewportSpans * 100)}svh`;
const visionTrackLengthDesktop = `${String(totalVisionTrackViewportSpans * 100)}lvh`;

const getVisionTrackProgressesFromSectionProgress = (sectionProgress: number) => {
  const clampedSectionProgress = clampUnitInterval(sectionProgress);
  let segmentStart = 0;

  return visionTrackViewportSpans.map((span) => {
    const normalizedSpan = span / totalVisionTrackViewportSpans;
    const segmentEnd = segmentStart + normalizedSpan;
    const segmentProgress = clampUnitInterval(
      (clampedSectionProgress - segmentStart) / normalizedSpan
    );

    segmentStart = segmentEnd;

    return segmentProgress;
  });
};

const resolveCssLengthToPx = (
  value: string,
  contextElement: HTMLElement = document.documentElement
) => {
  const trimmedValue = value.trim();

  if (trimmedValue.length === 0) {
    return 0;
  }

  if (/^-?\d*\.?\d+$/.test(trimmedValue)) {
    const numericValue = Number.parseFloat(trimmedValue);

    return Number.isFinite(numericValue) ? numericValue : 0;
  }

  const measurementElement = document.createElement('div');

  measurementElement.style.position = 'fixed';
  measurementElement.style.inset = '0 auto auto 0';
  measurementElement.style.width = '0';
  measurementElement.style.height = trimmedValue;
  measurementElement.style.margin = '0';
  measurementElement.style.padding = '0';
  measurementElement.style.border = '0';
  measurementElement.style.overflow = 'hidden';
  measurementElement.style.visibility = 'hidden';
  measurementElement.style.pointerEvents = 'none';

  contextElement.append(measurementElement);

  const resolvedValue = measurementElement.getBoundingClientRect().height;

  measurementElement.remove();

  return Number.isFinite(resolvedValue) ? resolvedValue : 0;
};

const getRenderedVisionTrackProgresses = (
  progresses: readonly number[],
  useCoarseProgress: boolean
) =>
  progresses.map((progress) => {
    const clampedProgress = clampUnitInterval(progress);

    if (!useCoarseProgress) {
      return clampedProgress;
    }

    if (clampedProgress >= visionTrackExitThreshold) {
      return 1;
    }

    if (clampedProgress >= visionTrackShowThreshold) {
      return visionTrackShowThreshold;
    }

    return 0;
  });

const areVisionTrackProgressesEqual = (left: readonly number[], right: readonly number[]) =>
  left.length === right.length && left.every((progress, index) => progress === right[index]);

const getActiveVisionItemIndex = (progresses: readonly number[]) => {
  for (let index = visionItems.length - 1; index >= 0; index -= 1) {
    if ((progresses[index] ?? 0) >= visionTrackShowThreshold) {
      return index;
    }
  }

  return 0;
};

const getVisionItemStates = (progresses: readonly number[]): VisionTrackAnimationState[] => {
  const firstTrackProgress = progresses[0] ?? 0;
  const activeVisionItemIndex = getActiveVisionItemIndex(progresses);

  return visionItems.map((_, index) => {
    if (index === 0 && firstTrackProgress < visionTrackShowThreshold) {
      return 'show';
    }

    if (index < activeVisionItemIndex) {
      return 'exit';
    }

    if (index === activeVisionItemIndex) {
      return 'show';
    }

    return 'hidden';
  });
};

const getSpheresSceneIndex = (progresses: readonly number[]) => {
  if ((progresses[0] ?? 0) < visionTrackShowThreshold) {
    return 0;
  }

  const activeVisionItemIndex = getActiveVisionItemIndex(progresses);

  if (activeVisionItemIndex <= 0) {
    return 1;
  }

  if (activeVisionItemIndex === 1) {
    return 2;
  }

  return 3;
};

gsap.registerPlugin(ScrollTrigger);

export const LandingVisionSection = ({ onSpheresSceneIndexChange }: LandingVisionSectionProps) => {
  const [indicatorVisionTrackProgresses, setIndicatorVisionTrackProgresses] = useState<number[]>(
    () => visionItems.map(() => 0)
  );
  const [renderedVisionTrackProgresses, setRenderedVisionTrackProgresses] = useState<number[]>(() =>
    visionItems.map(() => 0)
  );
  const previousSpheresSceneIndexRef = useRef(0);
  const sectionRef = useRef<HTMLElement | null>(null);
  const scrollTrackRef = useRef<HTMLDivElement | null>(null);
  const stickyPanelRef = useRef<HTMLDivElement | null>(null);
  const visionTrackProgressesRef = useRef(visionItems.map(() => 0));
  const committedIndicatorVisionTrackProgressesRef = useRef(visionItems.map(() => 0));
  const committedRenderedVisionTrackProgressesRef = useRef(visionItems.map(() => 0));
  const activeVisionItemIndex = useMemo(
    () => getActiveVisionItemIndex(renderedVisionTrackProgresses),
    [renderedVisionTrackProgresses]
  );
  const visionItemStates = useMemo(
    () => getVisionItemStates(renderedVisionTrackProgresses),
    [renderedVisionTrackProgresses]
  );
  const spheresSceneIndex = useMemo(
    () => getSpheresSceneIndex(renderedVisionTrackProgresses),
    [renderedVisionTrackProgresses]
  );

  useEffect(() => {
    if (previousSpheresSceneIndexRef.current === spheresSceneIndex) {
      return;
    }

    previousSpheresSceneIndexRef.current = spheresSceneIndex;
    onSpheresSceneIndexChange(spheresSceneIndex);
  }, [onSpheresSceneIndexChange, spheresSceneIndex]);

  useEffect(() => {
    const section = sectionRef.current;
    const scrollTrack = scrollTrackRef.current;
    const stickyPanel = stickyPanelRef.current;
    const scrollRoot = document.getElementById(LANDING_SCROLL_ROOT_ID);

    if (!section || !scrollTrack || !stickyPanel || !scrollRoot) {
      return;
    }

    let frameId: number | null = null;
    let refreshFrameId: number | null = null;
    let refreshTimeoutId: number | null = null;
    const coarseProgressQuery = window.matchMedia(
      '(hover: none), (pointer: coarse), (max-width: 767px)'
    );
    const useCoarseProgress = coarseProgressQuery.matches;

    const getPinOffset = () => {
      const stickyTop = resolveCssLengthToPx(
        window.getComputedStyle(section).getPropertyValue('--landing-vision-top-offset'),
        section
      );

      return Number.isFinite(stickyTop) ? stickyTop : 0;
    };

    const commitVisionTrackProgresses = () => {
      frameId = null;
      const nextIndicatorProgresses = getRenderedVisionTrackProgresses(
        visionTrackProgressesRef.current,
        false
      );
      const nextRenderedProgresses = getRenderedVisionTrackProgresses(
        visionTrackProgressesRef.current,
        useCoarseProgress
      );
      const hasIndicatorProgressChanged = !areVisionTrackProgressesEqual(
        committedIndicatorVisionTrackProgressesRef.current,
        nextIndicatorProgresses
      );
      const hasRenderedProgressChanged = !areVisionTrackProgressesEqual(
        committedRenderedVisionTrackProgressesRef.current,
        nextRenderedProgresses
      );

      if (!hasIndicatorProgressChanged && !hasRenderedProgressChanged) {
        return;
      }

      if (hasIndicatorProgressChanged) {
        committedIndicatorVisionTrackProgressesRef.current = nextIndicatorProgresses;

        setIndicatorVisionTrackProgresses((currentProgresses) => {
          if (areVisionTrackProgressesEqual(currentProgresses, nextIndicatorProgresses)) {
            return currentProgresses;
          }

          return nextIndicatorProgresses;
        });
      }

      if (!hasRenderedProgressChanged) {
        return;
      }

      committedRenderedVisionTrackProgressesRef.current = nextRenderedProgresses;

      setRenderedVisionTrackProgresses((currentProgresses) => {
        if (areVisionTrackProgressesEqual(currentProgresses, nextRenderedProgresses)) {
          return currentProgresses;
        }

        return nextRenderedProgresses;
      });
    };

    const requestVisionTrackProgressCommit = () => {
      if (frameId !== null) {
        return;
      }

      frameId = window.requestAnimationFrame(commitVisionTrackProgresses);
    };

    const updateVisionTrackProgresses = (sectionProgress: number) => {
      const nextProgresses = getVisionTrackProgressesFromSectionProgress(sectionProgress);

      if (areVisionTrackProgressesEqual(visionTrackProgressesRef.current, nextProgresses)) {
        return;
      }

      visionTrackProgressesRef.current = nextProgresses;

      const nextRenderedProgresses = getRenderedVisionTrackProgresses(
        visionTrackProgressesRef.current,
        useCoarseProgress
      );

      if (
        areVisionTrackProgressesEqual(
          committedIndicatorVisionTrackProgressesRef.current,
          visionTrackProgressesRef.current
        ) &&
        areVisionTrackProgressesEqual(
          committedRenderedVisionTrackProgressesRef.current,
          nextRenderedProgresses
        )
      ) {
        return;
      }

      requestVisionTrackProgressCommit();
    };

    const refreshVisionTrackTriggers = () => {
      refreshFrameId = null;
      ScrollTrigger.refresh();
    };

    const scheduleVisionTrackRefresh = (delay = 120) => {
      if (refreshTimeoutId !== null) {
        window.clearTimeout(refreshTimeoutId);
      }

      refreshTimeoutId = window.setTimeout(() => {
        refreshTimeoutId = null;

        if (refreshFrameId !== null) {
          window.cancelAnimationFrame(refreshFrameId);
        }

        refreshFrameId = window.requestAnimationFrame(refreshVisionTrackTriggers);
      }, delay);
    };

    const gsapContext = gsap.context(() => {
      ScrollTrigger.create({
        scroller: scrollRoot,
        trigger: scrollTrack,
        pin: stickyPanel,
        pinType: 'fixed',
        pinSpacing: false,
        start: () => `top top+=${getPinOffset().toFixed(2)}`,
        end: () => `bottom bottom+=${getPinOffset().toFixed(2)}`,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onRefresh: (self) => {
          updateVisionTrackProgresses(self.progress);
        },
        onUpdate: (self) => {
          updateVisionTrackProgresses(self.progress);
        },
        // markers: true,
      });
    }, section);

    void document.fonts.ready.then(() => {
      scheduleVisionTrackRefresh(0);
    });
    scheduleVisionTrackRefresh(0);

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }

      if (refreshFrameId !== null) {
        window.cancelAnimationFrame(refreshFrameId);
      }

      if (refreshTimeoutId !== null) {
        window.clearTimeout(refreshTimeoutId);
      }

      gsapContext.revert();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      id="vision"
      className="
        landing-container relative mx-auto min-h-svh max-w-400
        md:min-h-dvh
        pointer-coarse:pointer-events-none
      "
      aria-labelledby="landing-vision-heading"
    >
      <div
        className="
          relative grid grid-cols-1
          lg:grid-cols-2
          xl:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)]
        "
      >
        <div className="relative grid">
          <div className="col-start-1 row-start-1">
            <div
              ref={scrollTrackRef}
              data-vision-track="section"
              style={
                {
                  '--vision-track-length': visionTrackLengthMobile,
                  '--vision-track-length-desktop': visionTrackLengthDesktop,
                } as CSSProperties
              }
              className="
                h-(--vision-track-length)
                lg:h-(--vision-track-length-desktop)
              "
            />
          </div>
          <div
            className="
              col-start-1 row-start-1
              max-md:pe-4
            "
          >
            <div
              ref={stickyPanelRef}
              className="
                flex h-(--landing-vision-height)
                lg:items-center
                pointer-fine:pointer-events-none
              "
            >
              <div
                className="
                  relative grid h-fit grid-rows-[repeat(2,min-content)] gap-y-3
                  sm:pe-8
                  md:-mt-6 md:gap-y-4
                  lg:-mt-10 lg:pe-0
                "
              >
                <LandingVisionProgressIndicator
                  exitThreshold={visionTrackExitThreshold}
                  progresses={indicatorVisionTrackProgresses}
                  showThreshold={visionTrackShowThreshold}
                  states={visionItemStates}
                  className="
                    top-1/2 ms-3 -translate-y-1/2
                    max-lg:left-full
                    sm:ms-0
                    md:me-3.5
                    lg:right-full lg:me-4.5
                    xl:me-6
                  "
                />
                <p
                  className="
                    relative ms-px text-sm/5 font-medium text-primary
                    md:ms-0.5 md:text-base/6
                    lg:ms-1
                  "
                >
                  Vision
                </p>
                {visionItems.map((item, index) => (
                  <div className="col-start-1 row-start-2" key={item.title}>
                    <LandingVisionItem {...item} isActive={index === activeVisionItemIndex} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
